import React, { useState, useEffect } from 'react'
import axios from 'axios'
import pubsub from 'pubsub-js'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Button,
  Paper,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Modal,
  TextField,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import speeex_logo_modal from '../../../assets/images/logon.svg'
import { getSurveyQuestions, SURVEY_INTRO } from './managerSurveyQuestions'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      alignItems: 'flex-end',
    },
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    width: '100%',
    maxWidth: '80vw',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },
  header: {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  logo: {
    width: '100%',
    height: 80,
    marginBottom: theme.spacing(2),
    objectFit: 'contain',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.4rem',
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#6c757d',
  },
  intro: {
    fontSize: '0.85rem',
    color: '#6c757d',
    marginTop: theme.spacing(1.5),
    lineHeight: 1.5,
    maxWidth: 640,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginLeft: 8,
    backgroundColor: '#17a2b8',
    color: 'white',
    textTransform: 'uppercase',
  },
  content: {
    padding: theme.spacing(3),
  },
  question: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    border: '1px solid #e9ecef',
  },
  questionText: {
    fontWeight: 500,
    fontSize: '0.95rem',
    color: '#495057',
    marginBottom: theme.spacing(1.5),
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  radioOption: {
    backgroundColor: 'white',
    borderRadius: 4,
    padding: theme.spacing(0.5, 2),
    border: '1px solid #ced4da',
    '&:hover': { borderColor: '#6c757d' },
  },
  radioOptionSelected: {
    borderColor: '#007bff',
    backgroundColor: '#e7f3ff',
  },
  textField: {
    '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
  },
  errorText: {
    color: '#dc3545',
    fontSize: '0.8rem',
    marginTop: 4,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #dee2e6',
  },
  submitButton: {
    backgroundColor: '#28a745',
    color: 'white',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': { backgroundColor: '#218838' },
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': { backgroundColor: '#5a6268' },
  },
  readOnlyBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#28a745',
    color: 'white',
    marginBottom: theme.spacing(2),
  },
  readOnlyAnswer: {
    backgroundColor: '#e9ecef',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    fontSize: '0.9rem',
    color: '#495057',
    marginTop: 4,
  },
}))

const SURVEY_TYPE_LABEL = {
  probation: 'Periudha Provuese',
  exit: 'Largim nga Kompania',
  renewal: 'Vazhdim i Kontratës',
}

const ManagerFeedbackSurveyModal = ({
  surveyId,
  surveyData,
  open,
  onClose,
  onSubmitted,
  readOnly = false,
}) => {
  const classes = useStyles()
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})

  // only fetch when the parent didn't already hand us the survey (cold deep-link)
  const { data: fetchedSurvey } = useQuery({
    queryKey: ['manager-survey', surveyId],
    queryFn: () => axios.get(`manager-feedback-surveys/${surveyId}`).then((r) => r.data.data),
    enabled: open && !!surveyId && !surveyData,
    staleTime: 60000,
  })

  const survey = surveyData || fetchedSurvey
  const surveyType = survey?.type
  // questions depend on the survey type (probation vs exit)
  const questions = getSurveyQuestions(surveyType)

  // hydrate the answers when viewing a completed survey
  useEffect(() => {
    if (survey?.status === 'completed') {
      const next = {}
      getSurveyQuestions(survey.type).forEach((q) => {
        next[q.inputName] = survey[q.inputName] || ''
      })
      setAnswers(next)
    }
  }, [survey])

  const submitMutation = useMutation({
    mutationFn: (body) =>
      axios.patch(`manager-feedback-surveys/${surveyId}/submit`, body).then((r) => r.data.data),
    onSuccess: (updated) => {
      pubsub.publish('showNotification', { message: 'Feedback-u u dërgua me sukses!', type: 'success' })
      onSubmitted?.(updated)
      onClose()
    },
    onError: () => {
      pubsub.publish('showNotification', { message: 'Ndodhi një gabim. Provoni përsëri.', type: 'error' })
    },
  })

  const setAnswer = (inputName, value) => {
    setAnswers((prev) => ({ ...prev, [inputName]: value }))
    setErrors((prev) => (prev[inputName] ? { ...prev, [inputName]: false } : prev))
  }

  const handleSubmit = () => {
    const newErrors = {}
    questions.forEach((q) => {
      if (q.required && !String(answers[q.inputName] ?? '').trim()) {
        newErrors[q.inputName] = true
      }
    })
    setErrors(newErrors)
    if (Object.keys(newErrors).length) {
      pubsub.publish('showNotification', {
        message: 'Ju lutemi plotësoni të gjitha fushat e detyrueshme.',
        type: 'error',
      })
      return
    }

    const body = {}
    questions.forEach((q) => { body[q.inputName] = answers[q.inputName] ?? '' })
    submitMutation.mutate(body)
  }

  const loading = submitMutation.isLoading
  const isCompleted = survey?.status === 'completed'
  const isReadOnly = readOnly || isCompleted

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <img src={speeex_logo_modal} alt='SPEEEX' className={classes.logo} />
          <Typography className={classes.title}>
            Feedback i Menaxherit
            {survey && (
              <span className={classes.badge}>{SURVEY_TYPE_LABEL[survey.type] || survey.type}</span>
            )}
          </Typography>
          {survey && (
            <Typography className={classes.subtitle}>
              Punonjësi: <strong>{survey.employee?.first_name} {survey.employee?.last_name}</strong>
              {' · '}
              {survey.employee?.function?.name}
            </Typography>
          )}
          {survey && SURVEY_INTRO[surveyType] && (
            <Typography className={classes.intro}>{SURVEY_INTRO[surveyType]}</Typography>
          )}
          {isCompleted && (
            <div style={{ marginTop: 8 }}>
              <span className={classes.readOnlyBadge}>✓ E plotësuar</span>
            </div>
          )}
        </div>

        <div className={classes.content}>
          {questions.map((question) => {
            if (question.type === 'radio') {
              return (
                <div key={question.inputName} className={classes.question}>
                  <Typography className={classes.questionText}>{question.name}</Typography>
                  {isReadOnly ? (
                    <div className={classes.readOnlyAnswer}>
                      {question.options.find((o) => o.value === answers[question.inputName])?.text || '—'}
                    </div>
                  ) : (
                    <>
                      <RadioGroup
                        className={classes.radioGroup}
                        value={answers[question.inputName] || ''}
                        onChange={(e) => setAnswer(question.inputName, e.target.value)}
                      >
                        {question.options.map((opt) => (
                          <FormControlLabel
                            key={opt.key}
                            value={opt.value}
                            control={<Radio size='small' color='primary' />}
                            label={opt.text}
                            className={`${classes.radioOption} ${
                              answers[question.inputName] === opt.value ? classes.radioOptionSelected : ''
                            }`}
                          />
                        ))}
                      </RadioGroup>
                      {errors[question.inputName] && (
                        <Typography className={classes.errorText}>Ju lutem zgjidhni një opsion</Typography>
                      )}
                    </>
                  )}
                </div>
              )
            }

            return (
              <div key={question.inputName} className={classes.question}>
                <Typography className={classes.questionText}>{question.name}</Typography>
                {isReadOnly ? (
                  <div className={classes.readOnlyAnswer}>{answers[question.inputName] || '—'}</div>
                ) : (
                  <>
                    <TextField
                      className={classes.textField}
                      variant='outlined'
                      placeholder='Shkruani përgjigjen tuaj...'
                      value={answers[question.inputName] || ''}
                      onChange={(e) => setAnswer(question.inputName, e.target.value)}
                      fullWidth
                      multiline
                      rows={2}
                      size='small'
                      required={question.required}
                    />
                    {errors[question.inputName] && (
                      <Typography className={classes.errorText}>Kjo fushë është e detyrueshme</Typography>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>

        <div className={classes.actions}>
          <Button onClick={onClose} className={classes.cancelButton} variant='contained'>
            {isReadOnly ? 'Mbyll' : 'Anulo'}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSubmit} className={classes.submitButton} variant='contained' disabled={loading}>
              {loading ? 'Duke dërguar...' : 'Dërgo Feedback-un'}
            </Button>
          )}
        </div>
      </Paper>
    </Modal>
  )
}

export default ManagerFeedbackSurveyModal
